import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { money } from '../lib/money';
import { HandHeart, CheckCircle } from '@phosphor-icons/react';

export default function Give() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [form, setForm] = useState({ donor_name: '', donor_email: '', amount: '', batch_year: '', website: '' });

  useEffect(() => {
    api.get(`/advancement/give/${code}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [code]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === 'paid') { setPaid(true); clearInterval(poll); }
      } catch { /* keep polling */ }
      if (tries > 10) clearInterval(poll);
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/advancement/give/${code}/checkout`, { ...form, amount: parseFloat(form.amount) });
      window.location.href = r.data.checkout_url;
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  };

  const presets = [1000, 5000, 10000, 25000];
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="give-card">
        <div className="flex items-center gap-2 mb-1">
          <HandHeart size={22} className="text-orange-600" weight="fill" />
          <h1 className="font-display text-xl font-extrabold tracking-tight">{info ? info.school_name : error ? 'Page not found' : 'Loading…'}</h1>
        </div>
        <p className="text-sm text-slate-500 mb-4">Alumni giving{info?.page_message ? ` — ${info.page_message}` : ' — every gift counts.'}</p>

        {sessionId && paid && (
          <div className="text-center py-6" data-testid="give-thankyou">
            <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold">Thank you for your gift!</p>
            <p className="text-sm text-slate-500 mt-1">A receipt has been emailed to you{info?.tax_note ? ` — ${info.tax_note}` : '.'}</p>
          </div>
        )}
        {sessionId && !paid && (
          <div className="text-center py-6 text-sm text-slate-500" data-testid="give-checking">Confirming your payment with Stripe…</div>
        )}

        {!sessionId && info && !info.enabled && <p className="text-sm text-rose-600" data-testid="give-disabled">This giving page is not accepting donations right now.</p>}
        {!sessionId && info && info.enabled && (
          <div className="space-y-3">
            <input data-testid="give-name-input" placeholder="Your name" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <input data-testid="give-email-input" placeholder="Email (for your receipt)" value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <input data-testid="give-batch-input" placeholder="Batch year (optional, e.g. 2015)" value={form.batch_year} onChange={(e) => setForm({ ...form, batch_year: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />
            <div className="flex gap-2 flex-wrap">
              {presets.map((p) => (
                <button key={p} data-testid={`give-preset-${p}`} onClick={() => setForm({ ...form, amount: String(p) })}
                  className={`text-xs rounded-full px-3 py-1.5 border ${form.amount === String(p) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>{money(p, info.currency)}</button>
              ))}
            </div>
            <input data-testid="give-amount-input" type="number" placeholder={`Amount (${info.currency})`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
            {error && <p className="text-xs text-rose-600" data-testid="give-error">{error}</p>}
            <button data-testid="give-donate-button" disabled={!form.donor_name || !form.donor_email || !form.amount || busy} onClick={submit}
              className="w-full bg-orange-600 hover:bg-orange-500 text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-40">
              {busy ? 'Redirecting to secure checkout…' : `Donate ${form.amount ? money(parseFloat(form.amount), info.currency) : ''}`}
            </button>
            <p className="text-[10px] text-slate-400 text-center">Secure card payment via Stripe{info.tax_note ? ` · ${info.tax_note}` : ''}</p>
          </div>
        )}
        {!info && error && <p className="text-sm text-rose-600" data-testid="give-notfound">{error}</p>}
      </div>
    </div>
  );
}
